// This page works as an authentication toolbox and gateway between the App and the Auth Service.
// Any page that may need authentication tools or user credentials, 
// must set this page as a parent (in Router.js)

import { React, useEffect, useState, createContext, useContext } from 'react';
import { useKeycloak } from '@react-keycloak/web'
import store from '../../redux/store'
import { expireSession, get_user_settings_from_database_and_save_user_to_cache } from './SessionService';
import { checkPermissions } from '../../helpers/checkPermissions';
import i18n from '../../localization/i18next';
import { Main_Loader } from 'monica-alexandria';
import { Navigate } from 'react-router-dom';

const protocol = process.env.REACT_APP_ERISED_FRONTEND_PROTOCOL;
const url = process.env.REACT_APP_ERISED_FRONTEND_HOST;
const port = process.env.REACT_APP_ERISED_FRONTEND_PORT;

import c from '../../config/index'

const clients = c;
const client = process.env.REACT_APP_CLIENT;

const authClient = process.env.REACT_APP_AUTH_CLIENT;

export const AuthContext = createContext();

////////////////////////////////////////////////////////////////////
// Performs authentication before proceeding, loads user credentials
////////////////////////////////////////////////////////////////////
export const GuardedRoute = ({children, ...props}) =>  {

    const { keycloak, initialized } = useKeycloak();
    const [loadedUser, setLoadedUser] = useState({
        auth: false,
        session: null,
        sessionResolved: false
    })
    const lang = i18n.language ? i18n.language : 'el';

    let currentUrl = window.location.pathname;

    const generate_redirect_link = (clientName) => {
        return(`${clients[clientName]}${currentUrl}`)
    }

    // Redirects to central app for login. After login, returns to client of choice.
    // Queryparams will be forwarded to client and each client decides how to use them.
    const login = (clientName, redirectUri) => {  

        // If no client is specified, after login we will stay on central application
        if (!clientName) clientName = authClient;
        if (!redirectUri) redirectUri = clients[authClient];

        // deletes user credentials from cache
        expireSession();

        if (typeof keycloak?.login === 'function') {
            keycloak.login({
                redirectUri,
                locale: lang
            });
            return;
        }

        window.location.assign(`${clients[clientName]}/${lang}/auth/?redirectUri=${redirectUri}`);
    }

    const logout = (clientName) => {

        if (!clientName) clientName = 'dolores';

        // deletes user credentials from cache
        expireSession();

        let logoutUrl = keycloak.createLogoutUrl({redirectUri : clients[clientName],
                                                locale: lang});
        window.location.assign(logoutUrl);
    }

    const register = () => {

        // deletes user credentials from cache
        expireSession();

        let registerUrl = keycloak.createLoginUrl({action:'register', redirectUri : generate_redirect_link(),
                                                locale: lang})
        window.location.assign(registerUrl)
    }

    useEffect(() => {
        if (!initialized) {
            return;
        }

        if(!keycloak.authenticated && props.shouldRedirect) {
            login(authClient, window?.location?.href)
            return;
        }

        if (!keycloak.authenticated) {
            setLoadedUser({
                auth: false,
                session: null,
                sessionResolved: true
            });
            return;
        }

        keycloak.loadUserInfo().then((user) => {
            if(!user.email_verified) window.location.assign(`${protocol}${url}${port}/${lang}/verify-account?redirectUrl=${generate_redirect_link(client)}`)
            store.dispatch({type:'KEYCLOAK', payload: {user, token: keycloak.token}});
            get_user_settings_from_database_and_save_user_to_cache(keycloak, user)
                .then(data => {
                    setLoadedUser({
                        auth: keycloak.authenticated,
                        session: data || user,
                        sessionResolved: true
                    })
                })
        }).catch(() => {
            expireSession();
            setLoadedUser({
                auth: keycloak.authenticated,
                session: null,
                sessionResolved: true
            });
        })
    }, [initialized, keycloak, props.shouldRedirect])

    useEffect(() => {

        keycloak.onTokenExpired = () => {
            keycloak.updateToken(-1)
            .then(refreshed => {
                console.log('[test tokens] onTokenExpired +  updateTokenfired!')
            })
        };
        keycloak.onAuthRefreshSuccess = () => {
            console.log('[test tokens] onAuthRefreshSuccess fired!')
            store.dispatch({type:'KEYCLOAK', payload: {user: store.getState().user?.user, token: keycloak.token}})
        }
        
    }, [keycloak])

    const currentUser = keycloak.authenticated
        ? loadedUser.session || store.getState()?.user?.user || null
        : null;
    const hasAccess = checkPermissions(props.role, currentUser?.roles || []);

    if (!initialized || !loadedUser.sessionResolved) return <Main_Loader />
    if (props?.role && !keycloak.authenticated) return <Main_Loader />
    if (props?.role && keycloak.authenticated && !hasAccess) {
        return <Navigate to={`/${lang}/access-denied`} replace />
    }

    return (
        <AuthContext.Provider value={{
            login: (params, url) => login(params, url),
            register: () => register(),
            logout: () => logout(),
            login_with_custom_parameters: (params) => login(params),
            authenticated: keycloak.authenticated,
            user: currentUser
        }}>
            {children}
        </AuthContext.Provider>
    )
}
